import { Address, AddressType, Frequency, HomeDto } from '@lmc/ts-common'
import { FormInstance } from 'antd'
import { isBoolean } from 'lodash'
import { Dispatch, SetStateAction } from 'react'
import {
  getDateTimeInZone,
  TIMEZONE,
  getDateWithoutUTC,
} from '../utils.datetime'
import dayjs from 'dayjs'
import { SHIFT, VISIT } from '../constants'

export type ModalSectionProps = {
  formData?: Record<string, any>
  form?: FormInstance<any>
  setFormData?: Dispatch<SetStateAction<any>>
}

export type RotaPatternModalType = 'shift' | 'visit'

export const getHasReoccurence = (
  formData: Record<string, any> | undefined,
) => {
  const hasReoccurenceDefined = isBoolean(formData?.reoccurenceTooglePattern)

  const hasReoccurence = hasReoccurenceDefined
    ? formData?.reoccurenceTooglePattern === true
    : undefined

  return hasReoccurence
}

export const getParentTypeOfFrequency = (values: { frequency: Frequency }) => {
  const frequency = values?.frequency

  if (
    [
      Frequency.DAILY,
      Frequency.EVERY_X_DAYS,
      Frequency.SPECIFIC_WEEKDAYS,
    ].includes(frequency)
  ) {
    return Frequency.DAILY
  }

  if ([Frequency.EVERY_X_WEEKS, Frequency.WEEKLY].includes(frequency)) {
    return Frequency.WEEKLY
  }

  if (
    [
      Frequency.EVERY_X_MONTHS,
      Frequency.MONTHLY,
      Frequency.SPECIFIC_DAY_IN_MONTH,
    ].includes(frequency)
  ) {
    return Frequency.MONTHLY
  }

  return undefined
}

export const getRotaPatternSubmitValues = ({
  type,
  home,
  values,
}: {
  type?: RotaPatternModalType
  home?: HomeDto
  values?: any
}) => {
  const isShift = type === SHIFT

  const hasReoccurence = getHasReoccurence(values)

  const hasEndDate =
    values?.hasPatternEndDate === true ||
    (hasReoccurence && !!values?.patternEnd)

  const transformedValues = {
    subjectId: values?.subjectId,
    ...(isShift && { subjectId: home?.id }),
    ...(values?.fundingTypeId && { fundingTypeId: values?.fundingTypeId }),
    ...(values?.newFundingSource && {
      newFundingSource: values?.newFundingSource,
    }),
    addressId: values?.addressId,
    ...(values?.newAddress && { newAddress: values.newAddress }),
    title: values?.title,
    slotNotes: values?.slotNotes,
    numberSupply: values?.numberSupply,
    flexiType: !!values?.flexiType,
    homeId: home?.id,
    plannedDuration: hasReoccurence ? values?.plannedDuration : 1,
    patternStart: getDateWithoutUTC(values?.patternStart),
    ...(hasEndDate &&
      hasReoccurence && {
        patternEnd: getDateWithoutUTC(values?.patternEnd),
      }),
    teamMembers: values?.teamMembers,
    skills: values?.skills,
    siteShift: isShift,
    instanceTypeId: values?.instanceTypeId,
    ...(values?.newInstanceType && {
      newInstanceType: values?.newInstanceType,
    }),
    ...getSubmitFrequencyValue(values),
    timezone: TIMEZONE,
    startTime: values?.startTime,
    endTime: values?.endTime,
  }

  return transformedValues
}

export const getPatternFormInitialValues = (values: any) => {
  const parentTypeOfFrequency = getParentTypeOfFrequency(values)

  const interval =
    values?.dailyPattern?.interval ||
    values?.weeklyPattern?.interval ||
    values?.monthlyPattern?.interval

  const specificDays =
    values?.dailyPattern?.days ||
    values?.weeklyPattern?.days ||
    values?.monthlyPattern?.days

  // utc() is called here to normalize antd date/time picker to understand correct behaviour, to avoid current user zone effect
  return {
    ...values,
    interval,
    specificDays,
    frequency: parentTypeOfFrequency,
    patternStart: getDateTimeInZone(values?.patternStart).utc(),
    patternEnd: getDateTimeInZone(values?.patternEnd).utc(),
    reoccurenceTooglePattern: values?.frequency !== Frequency.ONE_OFF,
    hasPatternEndDate: !!values?.patternEnd,
    type: values?.siteShift === true ? SHIFT : VISIT,
  }
}

export const getNewAddress = (address: Address, withType = false) => {
  if (!address) {
    return ''
  }

  const addressTypeLabel = AddressType[address.type]

  const addressParts = []

  if (address?.number) addressParts.push(address.number)
  if (address?.name) addressParts.push(address.name)
  if (address?.street1) addressParts.push(address.street1)
  if (address?.street2) addressParts.push(address.street2)
  if (address?.suburb) addressParts.push(address.suburb)
  if (address?.state) addressParts.push(address.state)
  if (address?.postcode) addressParts.push(address.postcode)
  if (withType && addressTypeLabel) addressParts.push(addressTypeLabel)

  const formattedAddress = addressParts.join(', ')

  return formattedAddress.replace(/_/g, ' ')
}
export const getNewAddressById = (addresses: Address[], id: string) => {
  // @ts-ignore
  const foundAddress = addresses?.find((ad) => ad.id === id)

  return getNewAddress(foundAddress as Address)
}

export const getSubmitFrequencyValue = (values: any) => {
  const hasReoccurence = getHasReoccurence(values)

  const frequency = values?.frequency

  const hasOneInterval = values?.interval === 1

  const hasFullWeekDays = values?.specificDays?.length === 7

  // One Off: No recurrence
  let frequencyOutput = {
    frequency: Frequency.ONE_OFF,
    dailyPattern: undefined,
    weeklyPattern: undefined,
    monthlyPattern: undefined,
  }

  if (hasReoccurence) {
    // DAILY FREQUENCY TYPES
    // Daily
    if (frequency === Frequency.DAILY && hasOneInterval && hasFullWeekDays) {
      frequencyOutput = {
        frequency: Frequency.DAILY,
        // @ts-ignore
        dailyPattern: undefined,
        weeklyPattern: undefined,
        monthlyPattern: undefined,
      }
    }

    // Every X Days: Uses the daily pattern with an interval.
    if (frequency === Frequency.DAILY && !hasOneInterval) {
      frequencyOutput = {
        frequency: Frequency.EVERY_X_DAYS,
        // @ts-ignore
        dailyPattern: {
          interval: values?.interval,
        },
        weeklyPattern: undefined,
        monthlyPattern: undefined,
      }
    }

    // Has API issue
    // RotaPattern validation failed: dailyPattern.interval: Path `interval` is required.\n
    // Specific Weekdays: Uses the daily pattern with specific days.
    if (frequency === Frequency.DAILY && hasOneInterval && !hasFullWeekDays) {
      frequencyOutput = {
        frequency: Frequency.SPECIFIC_WEEKDAYS,
        // @ts-ignore
        dailyPattern: {
          days: values?.specificDays,
        },
        weeklyPattern: undefined,
        monthlyPattern: undefined,
      }
    }

    // WEEKLY FREQUENCY TYPES
    // Weekly: Can specify an interval (number of weeks) and specific days of the week.
    if (hasOneInterval && values?.frequency === Frequency.WEEKLY) {
      frequencyOutput = {
        frequency: Frequency.WEEKLY,
        dailyPattern: undefined,
        // @ts-ignore
        weeklyPattern: {
          interval: values?.interval,
          days: values?.specificDays,
        },
        monthlyPattern: undefined,
      }
    }

    // Every X Weeks: Uses the weekly pattern with an interval and specific days.
    if (!hasOneInterval && values?.frequency === Frequency.WEEKLY) {
      frequencyOutput = {
        frequency: Frequency.EVERY_X_WEEKS,
        dailyPattern: undefined,
        // @ts-ignore
        weeklyPattern: {
          interval: values?.interval,
          days: values?.specificDays,
        },
        monthlyPattern: undefined,
      }
    }

    // MONTHLY FREQUENCY TYPES
    // Monthly: Uses the monthly pattern with a specific day of the month and an interval.
    if (hasOneInterval && values?.frequency === Frequency.MONTHLY) {
      frequencyOutput = {
        frequency: Frequency.MONTHLY,
        dailyPattern: undefined,
        weeklyPattern: undefined,
        // @ts-ignore
        monthlyPattern: {
          interval: values?.interval,
          dayOfMonth: { day: +dayjs(values?.patternStart).format('D') },
        },
      }
    }

    // Every X Months: Similar to monthly but with a broader interval.
    if (!hasOneInterval && values?.frequency === Frequency.MONTHLY) {
      frequencyOutput = {
        frequency: Frequency.EVERY_X_MONTHS,
        dailyPattern: undefined,
        weeklyPattern: undefined,
        // @ts-ignore
        monthlyPattern: {
          interval: values?.interval,
          dayOfMonth: { day: +dayjs(values?.patternStart).format('D') },
        },
      }
    }

    // One more to be tested
    // Specific Day in the Month: Simplified monthly pattern occurring on a specific day each month.
  }

  return frequencyOutput
}

export const getGenerateDescriptionPattern = ({
  type,
  values,
}: {
  type?: RotaPatternModalType
  home?: HomeDto
  values?: any
}) => {
  const isShift = type === SHIFT

  const hasReoccurence = getHasReoccurence(values)

  const hasEndDate = values?.hasPatternEndDate === true

  const transformedValues = {
    flexiType: !!values?.flexiType,
    plannedDuration: hasReoccurence ? values?.plannedDuration : 1,
    patternStart: getDateWithoutUTC(values?.patternStart),
    ...(hasEndDate &&
      hasReoccurence && {
        patternEnd: getDateWithoutUTC(values?.patternEnd),
      }),
    ...(values?.startTime && {
      startTime: values?.startTime,
    }),
    ...(values?.endTime && {
      endTime: values?.endTime,
    }),
    siteShift: isShift,
    ...getSubmitFrequencyValue(values),
    timezone: TIMEZONE,
  }

  return transformedValues
}
